import React from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import styles from './styles/EliqHomeProfile.module.scss';

import { ConsumptionApp, OmaLummeApp } from '../../omaLummeContext';

import Button from '../Common/Button';
import Dialog from '../Common/Dialog';
import Loading from '../Common/Loading';
import { closeMenuIconBlack } from '../../icons';

import { EliqHomeProfile as EliqHomeProfileType, EliqProfileItem } from '../../types/eliq';
import { generateAddress } from '../../utils/address';
import { patchEliqHomeProfile } from '../../actions/consumption';

type EliqHomeProfileProps = {
  open: boolean;
  setShowHomeProfile: (value: React.SetStateAction<boolean>) => void;
};

type QuantityProps = {
  type: 'cooking' | 'appliances' | 'fridgesAndFreezers' | 'saunasAndHotTubs' | 'extras';
  homeProfileData?: EliqHomeProfileType;
  setProfileData: (value: React.SetStateAction<EliqHomeProfileType | undefined>) => void;
};
type QuantityRowProps = {
  category: EliqProfileItem;
  type: 'cooking' | 'appliances' | 'fridgesAndFreezers' | 'saunasAndHotTubs' | 'extras';
  profileData?: EliqHomeProfileType;
  setProfileData: (value: React.SetStateAction<EliqHomeProfileType | undefined>) => void;
};

const QuantityRow: React.FunctionComponent<QuantityRowProps> = (props: QuantityRowProps) => {
  const { t } = useTranslation();

  const { category, type, profileData, setProfileData } = props;
  const defaultValue = !Array.isArray(category.value) && typeof category.value === 'number' ? category.value : 0;
  const [value, setValue] = React.useState<number>(defaultValue);

  React.useEffect(() => {
    if (profileData) {
      const newData = { ...profileData };

      const item = newData[type].find((c) => c.key === category.key);
      if (item) {
        item.value = value;
      }

      setProfileData(newData);
    }
  }, [value]);

  return (
    <div className={styles.quantityRow}>
      <span>{t(`consumption:eliq.profile-${category.key}`)}</span>

      <div className={styles.quantity}>
        <button
          type="button"
          aria-disabled={value <= 0}
          onClick={() => {
            if (value > 0) {
              setValue(value - 1);
            }
          }}
        >
          -
        </button>
        <input
          type="number"
          min={category.limits?.min}
          max={category.limits?.max}
          value={value}
          onChange={(e) => {
            if (!category.limits || parseInt(e.target.value) <= category.limits?.max) {
              setValue(parseInt(e.target.value));
            } else {
              setValue(category.limits.max);
            }
          }}
        />
        <button
          className={styles.increase}
          aria-disabled={category.limits !== null && category.limits && value >= category.limits?.max}
          type="button"
          onClick={(e) => {
            // @ts-ignore
            if (e.target.ariaDisabled === 'false') {
              setValue(value + 1);
            }
          }}
        >
          +
        </button>
      </div>
    </div>
  );
};

const Quantity: React.FunctionComponent<QuantityProps> = (props: QuantityProps) => {
  const { homeProfileData, type, setProfileData } = props;

  const category = homeProfileData ? homeProfileData[type] : undefined;

  return (
    <>
      {category?.map((cat) => (
        <QuantityRow key={cat.key} category={cat} type={type} profileData={homeProfileData} setProfileData={setProfileData} />
      ))}
    </>
  );
};

export const EliqHomeProfile: React.FunctionComponent<EliqHomeProfileProps> = (props: EliqHomeProfileProps) => {
  const { t } = useTranslation();

  const { basicData, users, selectedMeteringPoint } = OmaLummeApp.useState();
  const { homeProfile } = ConsumptionApp.useState();

  const dataOwnerCustomerId = selectedMeteringPoint?.customerId;
  const actingCustomerId = users.activeUser.userId;
  const meteringPointId = selectedMeteringPoint?.meteringPointId;
  const meteringPointAddress = basicData?.find((d) => d.GSRN === meteringPointId)?.meteringpointAddress;
  const homeProfileData = homeProfile;

  const { open, setShowHomeProfile } = props;

  const [activePage, setActivePage] = React.useState<number>(0);
  const [profileData, setProfileData] = React.useState<EliqHomeProfileType | undefined>(cloneDeep(homeProfileData));
  const [profileSubmitting, setProfileSubmitting] = React.useState<boolean>(false);

  const submitForm = (
    e: React.FormEvent<HTMLFormElement> | undefined,
    close = true,
    path: string | undefined = undefined,
    value: number | string | string[] | EliqProfileItem[] | undefined = undefined,
  ): void => {
    if (e) {
      e.preventDefault();
    }

    async function updateHomeProfile(): Promise<void> {
      if (dataOwnerCustomerId && actingCustomerId && meteringPointId && profileData) {
        setProfileSubmitting(true);
        const status = await patchEliqHomeProfile(dataOwnerCustomerId, actingCustomerId, meteringPointId, profileData, path, value);

        if (status === 200) {
          setProfileSubmitting(false);
          if (close) {
            setShowHomeProfile(false);
          }
        } else {
          setProfileSubmitting(false);
          if (close) {
            setShowHomeProfile(false);
          }
        }
      }
    }
    updateHomeProfile();
  };

  const pageCount = 11;

  const keys = [
    'house_type',
    'living_area',
    'persons',
    'heating_type_primary',
    'heating_type_other',
    'hotwater_type',
    'cooking',
    'appliances',
    'fridgesAndFreezers',
    'saunasAndHotTubs',
    'extras',
  ];

  React.useEffect(() => {
    setProfileData(cloneDeep(homeProfileData));

    const validData = homeProfileData?.properties.filter((p) => {
      if (p.value && ((typeof p.value === 'number' && p.value > 0) || (typeof p.value !== 'number' && p.value.length > 0))) {
        return true;
      }
      return false;
    });

    if (validData && validData.length > 0) {
      setActivePage(pageCount + 1);
    } else {
      setActivePage(0);
    }
  }, [homeProfileData]);

  const renderPage = (pageNumber: number) => {
    const renderHeader = (key: string): React.JSX.Element => (
      <>
        <span className={`${styles.icon} ${styles[key]}`} />
        <h2 className="h1">{t(`consumption:eliq.profileTitle-${key}`)}</h2>
        <p>{t(`consumption:eliq.profileIngress-${key}`)}</p>
      </>
    );
    const renderFooter = (path: string, value: number | string | string[] | EliqProfileItem[]): React.JSX.Element => (
      <>
        <button
          className={styles.close}
          onClick={(): void => {
            setActivePage(pageCount + 1);
            submitForm(undefined, false, path, value);
          }}
        >
          {!profileSubmitting ? <img src={closeMenuIconBlack} alt={t('common:general.close')} /> : <Loading size="small" />}
        </button>
        <footer>
          <Button
            onClick={(e): void => {
              e.preventDefault();
              setActivePage(activePage - 1);
              submitForm(undefined, false, path, value);
            }}
            className="buttonWhite"
          >
            {t('common:general.previous')}
          </Button>
          <Button
            onClick={(e): void => {
              e.preventDefault();
              setActivePage(activePage + 1);
              submitForm(undefined, false, path, value);
            }}
          >
            {t('common:general.next')}
          </Button>
        </footer>
      </>
    );
    const renderRadiosAndCheckboxes = (key: string, type: 'radio' | 'checkbox'): React.JSX.Element => {
      const item = profileData?.properties.find((p) => p.key === key);
      const itemIndex = profileData?.properties.findIndex((p) => p.key === key);

      return (
        <>
          {item?.possibleValues?.map((v) => (
            <div key={v.type} className={`form-item ${type} ${styles.formItem}`}>
              <input
                type={type}
                id={v.type}
                name={key}
                onChange={(e) => {
                  const data = cloneDeep(profileData);

                  if (data) {
                    if (type === 'radio' && data.properties && itemIndex !== undefined) {
                      data.properties[itemIndex].value = v.value;
                    }
                    if (type === 'checkbox' && data.properties && itemIndex !== undefined) {
                      const arr = { ...data.properties[itemIndex] };

                      if (Array.isArray(arr.value) || arr.value === null) {
                        if (arr.value === null) {
                          arr.value = [];
                        }
                        if (item.value === null) {
                          item.value = [];
                        }
                        if (e.target.checked) {
                          // @ts-ignore
                          const value = [...item.value, v.value];
                          data.properties[itemIndex].value = value;
                        } else {
                          const newArr = arr.value.filter((s) => s !== v.value);
                          data.properties[itemIndex].value = newArr;
                        }
                      }
                    }

                    setProfileData(data);
                  }
                }}
                checked={type === 'checkbox' && Array.isArray(item.value) ? item.value.includes(v.value) : item.value === v.value}
              />
              <div className={`form-item-input ${styles.input}`}></div>
              <label htmlFor={v.type}>{t(`consumption:eliq.profile-${v.type}`)}</label>
            </div>
          ))}
        </>
      );
    };

    const updateNumberToProfileData = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const data = cloneDeep(profileData);

      if (data) {
        const item = data.properties?.find((p) => p.key === keys[index]);

        if (item) {
          if (parseInt(e.target.value) > 0) {
            if (item.limits) {
              if (parseInt(e.target.value) <= item.limits.max) {
                item.value = parseInt(e.target.value);
              } else {
                item.value = item.limits?.max;
              }
            } else {
              item.value = parseInt(e.target.value);
            }
          } else {
            item.value = parseInt(e.target.value);
          }
        }

        setProfileData(data);
      }
    };

    switch (pageNumber) {
      case 1: {
        const item = profileData?.properties.find((p) => p.key === keys[0]);
        return (
          <>
            {renderHeader(keys[0])}
            {renderRadiosAndCheckboxes(keys[0], 'radio')}
            {renderFooter(`properties/${keys[0]}`, item?.value ?? '')}
          </>
        );
      }
      case 2: {
        const item = profileData?.properties.find((p) => p.key === keys[1]);
        return (
          <>
            {renderHeader(keys[1])}
            <div className={styles.formItem}>
              <input
                type="number"
                min={0}
                id={`eliqProfile-${keys[1]}`}
                value={item?.value ?? ''}
                onChange={(e) => updateNumberToProfileData(1, e)}
              />{' '}
              <label htmlFor={`eliqProfile-${keys[1]}`}>{t(`consumption:eliq.profile-${keys[1]}_unit`)}</label>
            </div>
            {renderFooter(`properties/${keys[1]}`, item?.value ?? '')}
          </>
        );
      }
      case 3: {
        const item = profileData?.properties.find((p) => p.key === keys[2]);
        return (
          <>
            {renderHeader(keys[2])}
            <div className={styles.formItem}>
              <input
                type="number"
                min={0}
                id={`eliqProfile-${keys[2]}`}
                value={item?.value ?? ''}
                onChange={(e) => updateNumberToProfileData(2, e)}
              />{' '}
              <label htmlFor={`eliqProfile-${keys[2]}`}>{t(`consumption:eliq.profile-${keys[2]}_unit`)}</label>
            </div>
            {renderFooter(`properties/${keys[2]}`, item?.value ?? '')}
          </>
        );
      }
      case 4: {
        const item = profileData?.properties.find((p) => p.key === keys[3]);
        return (
          <>
            {renderHeader(keys[3])}
            {renderRadiosAndCheckboxes(keys[3], 'radio')}
            {renderFooter(`properties/${keys[3]}`, item?.value ?? '')}
          </>
        );
      }
      case 5: {
        const item = profileData?.properties.find((p) => p.key === keys[4]);
        return (
          <>
            {renderHeader(keys[4])}
            {renderRadiosAndCheckboxes(keys[4], 'checkbox')}
            {renderFooter(`properties/${keys[4]}`, item?.value ?? [])}
          </>
        );
      }
      case 6: {
        const item = profileData?.properties.find((p) => p.key === keys[5]);
        return (
          <>
            {renderHeader(keys[5])}
            {renderRadiosAndCheckboxes(keys[5], 'radio')}
            {renderFooter(`properties/${keys[5]}`, item?.value ?? '')}
          </>
        );
      }
      case 7: {
        const items = profileData?.cooking;
        return (
          <>
            {renderHeader(keys[6])}
            <Quantity homeProfileData={profileData} type="cooking" setProfileData={setProfileData} />
            {renderFooter(`${keys[6]}`, items ?? [])}
          </>
        );
      }
      case 8: {
        const items = profileData?.appliances;
        return (
          <>
            {renderHeader(keys[7])}
            <Quantity homeProfileData={profileData} type="appliances" setProfileData={setProfileData} />
            {renderFooter(`${keys[7]}`, items ?? [])}
          </>
        );
      }
      case 9: {
        const items = profileData?.fridgesAndFreezers;
        return (
          <>
            {renderHeader(keys[8])}
            <Quantity homeProfileData={profileData} type="fridgesAndFreezers" setProfileData={setProfileData} />
            {renderFooter(`${keys[8]}`, items ?? [])}
          </>
        );
      }
      case 10: {
        const items = profileData?.saunasAndHotTubs;
        return (
          <>
            {renderHeader(keys[9])}
            <Quantity homeProfileData={profileData} type="saunasAndHotTubs" setProfileData={setProfileData} />
            {renderFooter(`${keys[9]}`, items ?? [])}
          </>
        );
      }
      case 11: {
        const items = profileData?.extras;
        return (
          <>
            {renderHeader(keys[10])}
            <Quantity homeProfileData={profileData} type="extras" setProfileData={setProfileData} />
            {renderFooter(`${keys[10]}`, items ?? [])}
          </>
        );
      }
    }
  };

  return (
    <Dialog openModal={open} closeModal={() => setShowHomeProfile(false)}>
      <form className={styles.modal} onSubmit={(e): void => submitForm(e)}>
        {(!profileData || profileData.properties.length === 0) && (
          <button className={styles.close} onClick={(): void => setShowHomeProfile(false)}>
            <img src={closeMenuIconBlack} alt={t('common:general.close')} />
          </button>
        )}
        {(!profileData || profileData.properties.length === 0) && <Loading />}
        {profileData && profileData.properties.length > 0 && meteringPointAddress && (
          <>
            {activePage === 0 && (
              <div className={styles.start}>
                <span className={`${styles.icon} ${styles.house_type}`} />
                <h2 className="h1">{t('consumption:eliq.profileStartTitle')}</h2>
                <p className={styles.ingress}>{t('consumption:eliq.profileIngress')}</p>

                <footer>
                  <Button onClick={(): void => setActivePage(activePage + 1)}>{t('consumption:eliq.profileStart')}</Button>
                  <Button onClick={(): void => setShowHomeProfile(false)} className="buttonWhite">
                    {t('consumption:eliq.profileLater')}
                  </Button>
                </footer>
              </div>
            )}
            {activePage > 0 && activePage <= pageCount && (
              <div className={styles.page}>
                <header>
                  <span className={styles.pageNumber}>
                    {activePage}/{pageCount}
                  </span>
                  <div className={styles.pageIndicator}>
                    <span style={{ width: `${(activePage / pageCount) * 100}%` }} />
                  </div>
                </header>
                {renderPage(activePage)}
              </div>
            )}
            {activePage > pageCount && (
              <div className={styles.finish}>
                <button
                  className={styles.close}
                  onClick={(): void => {
                    setShowHomeProfile(false);
                    submitForm(undefined);
                  }}
                >
                  <img src={closeMenuIconBlack} alt={t('common:general.close')} />
                </button>
                <h2 className="h1">{t('consumption:eliq.profileFinishTitle')}</h2>

                <p>
                  {t('common:general.address')}
                  <br />
                  <strong>{generateAddress(meteringPointAddress, false)}</strong>
                </p>

                <div className={styles.finishGrid}>
                  {}
                  {keys.map((k: any, i) => {
                    let value: string | number = '';

                    if (i < 6) {
                      const rawValue = profileData.properties[i].value;

                      if (profileData.properties[i].dataType === 'number' || rawValue === null) {
                        if ((typeof rawValue !== 'object' && isNaN(parseInt(rawValue.toString()))) || rawValue === null) {
                          value = '';
                        } else {
                          value = `${rawValue} ${k === 'living_area' ? t(`consumption:eliq.profile-${k}_unit`) : ''}`;
                        }
                      } else if (profileData.properties[i].dataType === 'multiple_select') {
                        const arr = profileData.properties[i].value;

                        if (arr && typeof arr === 'object') {
                          arr.forEach((s, i) => {
                            value += t(`consumption:eliq.profile-${s}`);
                            if (i < arr.length - 1) {
                              value += ', ';
                            }
                          });
                        }
                      } else {
                        value = t(`consumption:eliq.profile-${profileData.properties[i].value}`);
                      }
                    }
                    if (i >= 6) {
                      // @ts-ignore
                      const filteredItems: EliqProfileItem[] = profileData[k].filter((i) => i.value > 0);

                      filteredItems.forEach((item, i) => {
                        value += t(`consumption:eliq.profile-${item.key}`);
                        if (filteredItems.length > 1 && i < filteredItems.length - 1) {
                          value += ', ';
                        }
                      });
                    }

                    return (
                      <button
                        key={k}
                        id="eliqProfileFinish"
                        className={styles.finishItem}
                        aria-disabled={profileSubmitting}
                        onClick={() => {
                          if (document.getElementById('eliqProfileFinish')?.ariaDisabled === 'false') {
                            setActivePage(i + 1);
                          }
                        }}
                      >
                        <span className={`${styles.icon} ${styles[k]}`} />
                        <div>
                          <span>{t(`consumption:eliq.profileTitle-${k}`)}</span>
                          <strong>
                            {value.length > 0 ? value : <span className={styles.finishAdd}>+ {t('consumption:eliq.profileFinishAdd')}</span>}
                          </strong>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <footer>
                  <Button
                    disabled={profileSubmitting}
                    type="submit"
                    onClick={(e): void => {
                      // @ts-ignore
                      if (e.target.ariaDisabled !== 'false') {
                        e.preventDefault();
                      }
                    }}
                  >
                    {t('common:general.save')}
                  </Button>
                </footer>
                {profileSubmitting && <Loading size="small" />}
              </div>
            )}
          </>
        )}
      </form>
    </Dialog>
  );
};
